import { useNavigate, useSearch } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useCurrentWorkspace } from "~/api/queries";
import { ABILITY } from "~/api/types";
import { Tabs } from "~/features/settings/controls";
import { AssistantsPanel } from "~/features/settings/sections/thinkers/AssistantsPanel";
import {
  FallbacksPanel,
  KeysPanel,
  LeasesPanel,
  MinePanel,
  RequestsPanel,
  SharedPanel,
  UsagePanel,
} from "~/features/settings/sections/thinkers/economy/EconomyPanels";
import { LocalPanel } from "~/features/settings/sections/thinkers/LocalPanel";
import { NodesPanel } from "~/features/settings/sections/thinkers/NodesPanel";
import { parseThinkerPanel } from "~/features/settings/sections/thinkers/ThinkerTabs";
import { ThreadsPanel } from "~/features/settings/sections/thinkers/threads/ThreadsPanel";
import { WorkspacePanel } from "~/features/settings/sections/thinkers/WorkspacePanel";
import { Text } from "~/foundation/components/Text";
import { usePolicy } from "~/policies/usePolicy";

type Tab =
  | "workspace"
  | "assistants"
  | "local"
  | "mine"
  | "shared"
  | "leases"
  | "requests"
  | "keys"
  | "fallbacks"
  | "usage"
  | "nodes"
  | "threads";
const TABS: readonly Tab[] = [
  "workspace",
  "assistants",
  "local",
  "mine",
  "shared",
  "leases",
  "requests",
  "keys",
  "fallbacks",
  "usage",
  "nodes",
  "threads",
];

/**
 * Who this workspace can hand work to.
 *
 * Three tabs over one idea, in the order a workspace actually configures them: the personas it
 * has adopted, the people it has made addressable, and the models it runs on its own hardware.
 * Workspace configuration writes use the server's abilities. Compute nodes are linked and managed by
 * the signed-in account itself; ordinary members can open this page and approve their own computers.
 *
 * Deep links (2026-10-10): `?tab=local|workspace|assistants` picks the tab, `?local=<thinkerId>`
 * / `?persona=<id>` opens that thinker's dialog on arrival — the research room's "Edit facets"
 * lands here. The search params only INITIALISE the dialog state (which stays in the panels);
 * closing the dialog clears the parameter.
 *
 * BRONCO Opus 02 adds the thinker economy: Mine (what this workspace owns, pools and lends) · Shared with me · Leases ·
 * Requests (borrows waiting for an answer) · Keys (its own provider keys) · Fallbacks · Usage. A refused call's sentence
 * links here as `?tab=shared&request=<thinker>`, which opens the request form for that thinker.
 *
 * BRONCO Opus 03 adds Threads (`?tab=threads&thread=<id>`; `/settings/agents/threads` redirects here): the standing
 * interests a thinker keeps thinking about, each tick a thinkers room — Opus 06 moves them into the assistant's stall.
 */
export function Thinkers() {
  const { t } = useTranslation();
  const search = useSearch({ strict: false }) as {
    tab?: unknown;
    local?: unknown;
    persona?: unknown;
    panel?: unknown;
    request?: unknown;
    thread?: unknown;
    pair?: unknown;
  };
  const navigate = useNavigate();
  const wanted = TABS.find((candidate) => candidate === search.tab);
  const [tab, setTab] = useState<Tab>(wanted ?? "workspace");
  const openLocal = typeof search.local === "string" && search.local ? search.local : null;
  const openPersona = typeof search.persona === "string" && search.persona ? search.persona : null;
  const openPanel = parseThinkerPanel(search.panel);
  const requestFor = typeof search.request === "string" && search.request ? search.request : null;
  const openThread = typeof search.thread === "string" && search.thread ? search.thread : null;
  const { data: workspace } = useCurrentWorkspace();
  const canAdminister = usePolicy(workspace)[ABILITY.workspaceUpdate] === true;
  const patch = (next: Record<string, string | undefined>) =>
    void navigate({
      search: (prev: Record<string, unknown>) => ({ ...prev, ...next }),
      replace: true,
    } as never);
  const choose = (next: Tab) => {
    setTab(next);
    patch({ tab: next });
  };

  return (
    <div className="flex flex-col gap-4">
      <Text variant="small">
        {t(
          "The thinkers your workspace can use — AI personas, teammates, and models running on your own machine.",
        )}
      </Text>

      <Tabs<Tab>
        value={tab}
        onChange={choose}
        options={[
          { value: "workspace", label: t("Workspace") },
          { value: "assistants", label: t("Assistants") },
          { value: "local", label: t("Local models") },
          { value: "mine", label: t("Mine") },
          { value: "shared", label: t("Shared with me") },
          { value: "leases", label: t("Leases") },
          { value: "requests", label: t("Requests") },
          { value: "keys", label: t("Keys") },
          { value: "fallbacks", label: t("Fallbacks") },
          { value: "usage", label: t("Usage") },
          { value: "nodes", label: t("Compute nodes") },
          { value: "threads", label: t("Threads") },
        ]}
      />

      {tab === "workspace" ? (
        <WorkspacePanel
          canAdminister={canAdminister}
          openPersonaId={openPersona}
          openPanel={openPanel}
          onDialogClose={() => patch({ persona: undefined, panel: undefined })}
        />
      ) : null}
      {tab === "assistants" ? <AssistantsPanel canAdminister={canAdminister} /> : null}
      {tab === "mine" ? <MinePanel canAdminister={canAdminister} /> : null}
      {tab === "shared" ? <SharedPanel requestFor={requestFor} /> : null}
      {tab === "leases" ? <LeasesPanel canAdminister={canAdminister} /> : null}
      {tab === "requests" ? <RequestsPanel canAdminister={canAdminister} /> : null}
      {tab === "keys" ? <KeysPanel canAdminister={canAdminister} /> : null}
      {tab === "fallbacks" ? <FallbacksPanel canAdminister={canAdminister} /> : null}
      {tab === "usage" ? <UsagePanel /> : null}
      {tab === "nodes" ? (
        <NodesPanel pairCode={typeof search.pair === "string" ? search.pair : undefined} />
      ) : null}
      {tab === "threads" ? (
        <ThreadsPanel
          canAdminister={canAdminister}
          openThreadId={openThread}
          onOpen={(id) => patch({ thread: id ?? undefined })}
        />
      ) : null}
      {tab === "local" ? (
        <LocalPanel
          canAdminister={canAdminister}
          openThinkerId={openLocal}
          openPanel={openPanel}
          onDialogClose={() => patch({ local: undefined, panel: undefined })}
          onOpenWorkspace={(id) => {
            setTab("workspace");
            patch({ tab: "workspace", local: undefined, persona: id });
          }}
        />
      ) : null}
    </div>
  );
}
